import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { moment } from '@lib/moment';

import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Router } from '@angular/router';

import { extractResponseBody } from '@lib/utils';
import { ServerApi } from '@services/server-api';
import { FleetCardsOffersService } from '@fleet-cards/offers/service';
import { Pusher } from '@app/services/pusher';

import { INotification } from './notification.interface';
import { IFleetCardOffer } from '@fleet-cards/offers/interfaces';

const endpoint: string = 'notification_center/notifications';

interface IServerAction {
  redirect_uri: string;
  is_query_params: boolean;
  query_params: {
    [key: string]: string;
  };
}

interface IServerNotification {
  id: number;
  category: string;
  body: string;
  subbody: string;
  important: boolean;
  action: IServerAction;
  sent_at: string;
  read_at: string;
  seen_at: string;
  created_at: string;
  updated_at: string;
}

interface IServerNotificationObject {
  notification: IServerNotification;
}

interface IServerResponseBody {
  notifications: IServerNotificationObject[];
}

interface IServerPutBody {
  id: number[];
  read?: boolean;
  seen?: boolean;
}

enum SERVER_SORT_FIELDS {
  ID = 'id',
  READ_AT = 'read_at',
  SENT_AT = 'sent_at',
  CREATED_AT = 'created_at',
  UPDATED_AT = 'updated_at',
}

enum SERVER_SORT_DIRECTION {
  ASC = 'asc',
  DESC = 'desc',
}

export interface IServerGetParams {
  unread?: boolean;
  important?: boolean;
  sort_field?: SERVER_SORT_FIELDS;
  sort_direction?: SERVER_SORT_DIRECTION;
  per_page?: number; // 1 to 100
  page_no?: number;
}

interface IMarkAllAsReadResponse {
  company_id: number;
  created_at: string;
  id: number;
  important: boolean;
  notification_body_id: number;
  read_at: string;
  seen_at: string;
  sent_at: string;
  updated_at: string;
  user_id: number;
}

export interface IPusherNotificationEvent {
  notification_center: string;
}

export function mapNotification(serverNotificationObject: IServerNotificationObject): INotification {
  const serverNotification = serverNotificationObject.notification;
  return {
    id: serverNotification.id,
    read: !!serverNotification.read_at,
    seen: !!serverNotification.seen_at,
    type: serverNotification.category,
    priority: serverNotification.important,
    text: serverNotification.body,
    subtext: serverNotification.subbody,
    createdAt: moment(new Date(serverNotification.created_at)),
    action: {
      redirectUri: serverNotification.action?.redirect_uri ?? '',
      isQueryParam: serverNotification.action?.is_query_params ?? false,
      queryParams: serverNotification.action?.query_params ?? {},
    },
  };
}

const CHANNEL_NAME = 'notification-center';
enum EVENTS {
  CREATE = 'create',
  UPDATE = 'update',
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsCenterService {
  protected pusher;

  constructor(
    private api: ServerApi,
    injector: Injector,
    private fleetCardsOffersService: FleetCardsOffersService,
    private router: Router,
  ) {
    this.pusher = injector.get(Pusher);
  }

  public get(params: IServerGetParams): Observable<INotification[]> {
    const paramsMap = new Map(Object.entries(params));
    return this.api
      .get(endpoint, paramsMap)
      .pipe(
        map(extractResponseBody),
        map(this.transformData.bind(this)),
      )
    ;
  }

  public put(params: IServerPutBody): Observable<INotification[]> {
    return this.api
      .put(endpoint, params)
      .pipe(
        map(extractResponseBody),
        map(this.transformData.bind(this)),
      );
  }

  public markNotificationsRead(notificationIds: number[]): Observable<INotification[]> {
    return this.put({
      id: notificationIds,
      read: true,
    });
  }

  public markAllNotificationsRead(): Observable<HttpEvent<IMarkAllAsReadResponse>> {
    return this.api
      .put(`${endpoint}/mark_all_as_read`, {});
  }

  public markNotificationsSeen(notificationIds: number[]): Observable<INotification[]> {
    return this.put({
      id: notificationIds,
      seen: true,
    });
  }

  public getPushNotifications(): Observable<IPusherNotificationEvent> {
    return this.pusher.getUserChannel(CHANNEL_NAME)
      .getEvent(`${CHANNEL_NAME}:${EVENTS.CREATE}`);
  }

  private transformData(data: IServerResponseBody): INotification[] {
    return data.notifications.map(mapNotification);
  }

  public handleNotificationQueryParams(queryParams: { [action: string]: string }): void {
    if (queryParams.promotional_campaign_open_detail_drawer) {
      this.fleetCardsOffersService
      .getOfferDetail(queryParams.promotion_campaign_id)
      .subscribe({
        next: (offer: IFleetCardOffer) => {
          this.fleetCardsOffersService.openDrawer$.next(offer);
          this.router.navigate([], {
            queryParams: {
              promotional_campaign_open_detail_drawer: null,
              promotion_campaign_id: null,
            },
            queryParamsHandling: 'merge',
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.router.navigate([], {
            queryParams: {
              promotional_campaign_open_detail_drawer: null,
              promotion_campaign_id: null,
            },
            queryParamsHandling: 'merge',
          });
        },
      });
    }
  }
}